/** @jsxImportSource @emotion/react */
import { isOrganization } from 'helpers/followUpProvidersHelper'
import { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import moment from 'moment'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'
import { Document, Page, pdfjs } from 'react-pdf'

import DateTimePicker from 'components/atoms/DateTimePicker'
import Input from 'components/atoms/Input'
import Upload from 'components/atoms/Upload'
import Toast from 'components/atoms/Toast'
import { toast } from 'react-toastify'
import {
  useLazyGetLabOrderQuery,
  useUploadHstReportMutation,
  useAttachWesperReportMutation,
  useRecognizeHstReportMutation,
} from 'store/services/labOrder'
import {
  useLazyGetLabPhysiciansQuery,
  useLazyGetEffectiveFollowUpPhysiciansQuery,
} from 'store/services/labPhysician'
import { Order } from 'store/types'
import type { HstReportRecognition } from 'ts-back-end/src/business/order/types/report.types'
import type { HstReportRecognitionResult } from 'ts-back-end/src/business/rx-recognition/types/types'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`

const MAX_DATES_OF_SERVICE = 3

// The AI section is the one thing in the modal that is not a plain form, so it
// gets a soft gradient card of its own — enough to draw the eye, no more.
const AiPanel = styled.div`
  position: relative;
  margin-bottom: 1.25rem;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #f3f6ff 0%, #f7f2ff 55%, #fff5fb 100%);
  border: 1px solid rgba(99, 102, 241, 0.22);
  transition: border-color 0.2s ease;

  &.is-busy {
    border-color: rgba(99, 102, 241, 0.55);
  }

  .ai-head {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  .ai-head-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .ai-body {
    margin-top: 0.9rem;
  }

  .ai-badge {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    border-radius: 10px;
    color: #fff;
    background: linear-gradient(135deg, #6366f1, #a855f7);
    font-size: 15px;
  }

  .ai-title {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #1e1b4b;
  }

  .ai-subtitle {
    font-size: 0.8rem;
    color: #4b5563;
  }

  .ai-file {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.7rem 0.9rem;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(99, 102, 241, 0.2);
  }

  .ai-file-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    color: #6366f1;
    background: rgba(99, 102, 241, 0.12);
    font-size: 16px;
  }

  .ai-file-text {
    min-width: 0;
    flex: 1;
  }

  .ai-file-name {
    font-weight: 600;
    font-size: 0.85rem;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-file-meta {
    font-size: 0.75rem;
    color: #6b7280;
  }

  .ai-notes {
    list-style: none;
    margin: 0.75rem 0 0;
    padding: 0.55rem 0.75rem;
    border-radius: 8px;
    font-size: 0.78rem;
    color: #92400e;
    background: rgba(254, 243, 199, 0.8);
    border: 1px solid rgba(217, 119, 6, 0.25);

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.4rem;

      + li {
        margin-top: 0.25rem;
      }

      i {
        margin-top: 2px;
        color: #d97706;
      }
    }
  }
`

// Total sleep time (TST) is held per night in whole minutes — the unit the
// report is read in — and edited as hours + minutes.
const minutesToHours = (minutes: number | null | undefined): string =>
  minutes == null ? '' : String(Math.floor(minutes / 60))
const minutesToRemainder = (minutes: number | null | undefined): string =>
  minutes == null ? '' : String(minutes % 60)
const toTotalMinutes = (hours: string, minutes: string): number | null => {
  if (hours === '' && minutes === '') return null
  const total = Number(hours || 0) * 60 + Number(minutes || 0)
  return Number.isFinite(total) && total >= 0 ? Math.round(total) : null
}

// SHA-256 hex of a file, the same digest the server stores with a scan. Null
// where the browser offers no SubtleCrypto (plain-http dev hosts).
const sha256 = async (file: File): Promise<string | null> => {
  try {
    if (!window.crypto?.subtle) return null
    const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

// Report types that must have an interpreting physician and interpreting date
// before the report can be uploaded (regular HST and the In-Lab PSG & Titration
// reports). Failed HST and any other type keep these fields optional.
const REPORT_TYPES_REQUIRING_INTERPRETATION = ['HST', 'PSG', 'TITRATION']

const FormGroup = styled.div`
  margin-bottom: 1rem;
`

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;

  .react-datepicker-wrapper {
    flex: 1;

    input {
      width: 100%;
      padding: 9px 8px;
      height: 38px;
    }
    svg {
      top: 12px !important;
      right: 10px !important;
    }
    button {
      margin-right: 20px;
    }
  }
`

const RemoveDateButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.15s;

  &:hover {
    background-color: #fee2e2;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  }
`

const AddDateButton = styled.button`
  background: none;
  border: 1px dashed #2c83bd;
  color: #2c83bd;
  cursor: pointer;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.15s;
  margin-top: 0.5rem;

  &:hover {
    background-color: #e8f4fc;
    border-color: #1a5276;
  }

  &:disabled {
    color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  }
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 0.75rem;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    margin-left: 0.75rem;
    cursor: pointer;
    font-weight: 500;
    color: #374151;
  }

  .checkbox-description {
    font-size: 12px;
    color: #6b7280;
    margin-left: 2rem;
    margin-top: 0.25rem;
  }
`

const Divider = styled.hr`
  margin: 1.5rem 0;
  border: 0;
  border-top: 1px solid #e5e7eb;
`

const SectionTitle = styled.h6`
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Row = styled.div`
  display: flex;
  gap: 1rem;

  > div {
    flex: 1;
  }
`

type WesperPrefill = {
  datesOfService: string[]
  // Minutes per night (from Wesper's recording time), aligned with datesOfService.
  testingTimes: (number | null)[]
  ahi: string
  interpretingDate: string | null
  // The physician staff picked on the order before it went to Wesper — authoritative.
  interpretingPhysicianId: number | null
  // Whoever signed the report on Wesper's side. Only a fallback: it is a Wesper-side
  // reviewer, so it often matches no physician in our directory.
  interpretingProviderName: string | null
}

interface ReportUploadModalProps {
  open: boolean
  onClose: () => void
  order: Order
  onUploadComplete?: () => void
  reportType: string
  /** When set, the modal renders the side-by-side Wesper review layout: form on the left
   * (pre-filled from `prefill`), the pending report PDF on the right, and the footer button
   * attaches the already-stored report instead of uploading a new file. */
  wesperAttach?: {
    sessionId: string
    fileUrl: string
    prefill: WesperPrefill
  }
}

type PhysicianOption = { value: string; label: string }

export const ReportUploadModal = ({
  open,
  onClose,
  order,
  onUploadComplete,
  reportType,
  wesperAttach,
}: ReportUploadModalProps) => {
  const [uploadReport] = useUploadHstReportMutation()
  const [attachWesperReport] = useAttachWesperReportMutation()
  const [recognizeReport] = useRecognizeHstReportMutation()
  const [getLabOrder] = useLazyGetLabOrderQuery()
  const uploadRef = useRef<HTMLInputElement>(null)
  const recognizeRef = useRef<HTMLInputElement>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const expectedFilesRef = useRef<string[] | null>(null)

  const referringPhysicianNpi = order?.physician?.npi ?? order?.verifiedPhysician?.npi

  // Fetch interpreting physicians from the main physicians directory
  const [getInterpretingPhysicians, { data: interpretingPhysiciansData, isLoading: isLoadingPhysicians }] =
    useLazyGetLabPhysiciansQuery()

  // Fetch follow-up physicians for the order's referring physician
  const [
    getFollowUpPhysicians,
    { data: effectiveFollowUpPhysiciansData, isLoading: isLoadingFollowUpPhysicians },
  ] = useLazyGetEffectiveFollowUpPhysiciansQuery()

  // Transform interpreting physicians data for Select component
  const interpretingPhysiciansOptions: PhysicianOption[] = interpretingPhysiciansData?.map((physician) => {
    const fullName = [physician.firstName, physician.middleName, physician.lastName]
      .filter(Boolean)
      .join(' ')
    return {
      value: physician.npi,
      label: fullName || physician.npi,
    }
  }) || []

  const followUpCareProviderOptions: PhysicianOption[] =
    effectiveFollowUpPhysiciansData?.items?.map((item) => {
      const p = item.followUpPhysician
      const fullName = [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(' ')
      return {
        value: String(item.followUpPhysicianId),
        label: isOrganization(p?.providerType)
          ? p?.organizationName || fullName
          : fullName || `Physician ${item.followUpPhysicianId}`,
      }
    }) ?? []
  const hasFollowUpPhysicians = followUpCareProviderOptions.length > 0

  // Form state
  const [datesOfService, setDatesOfService] = useState<(string | undefined)[]>([undefined])
  // TST per night, whole minutes, aligned with datesOfService.
  const [testingTimes, setTestingTimes] = useState<(number | null)[]>([null])
  const [ahi, setAhi] = useState<string | undefined>(undefined)
  // AI recognition: the report picked for it is held here and uploaded on
  // confirm, so the person never selects the same file twice.
  const [recognizedFile, setRecognizedFile] = useState<File | null>(null)
  // The scan the form is filled from — either just run, or the one stored on
  // the order from an earlier attempt. Its stored copy is what gets uploaded
  // when no fresh file was picked.
  const [recognition, setRecognition] = useState<HstReportRecognition | null>(null)
  // True when the picked file turned out to be the report already scanned.
  const [reusedScan, setReusedScan] = useState(false)
  const latestScanRef = useRef<HstReportRecognition | null>(null)
  const [isRecognizing, setIsRecognizing] = useState(false)
  const [recognitionNotes, setRecognitionNotes] = useState<string[]>([])
  const [interpretingPhysician, setInterpretingPhysician] = useState<PhysicianOption | null>(null)
  const [interpretingDate, setInterpretingDate] = useState<string | undefined>(undefined)
  const [includePapScript, setIncludePapScript] = useState<boolean>(true)
  const [includeDmeInfoInPap, setIncludeDmeInfoInPap] = useState<boolean>(true)
  const [apapMin, setApapMin] = useState<string | undefined>(undefined)
  const [apapMax, setApapMax] = useState<string | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const [followUpCareProvider, setFollowUpCareProvider] = useState<PhysicianOption | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [rotation, setRotation] = useState<number>(0)
  const [pdfScale, setPdfScale] = useState<number>(1)
  const [pdfLoadError, setPdfLoadError] = useState<boolean>(false)

  // Stable (primitive) view of wesperAttach.prefill so effects below don't re-run just because
  // the caller (PatientTesting) recomputes the wesperAttach object on every unrelated re-render.
  const wesperSessionId = wesperAttach?.sessionId
  const wesperPrefillDatesKey = wesperAttach?.prefill.datesOfService.join('|') ?? ''
  const wesperPrefillTestingTimesKey = wesperAttach?.prefill.testingTimes.join('|') ?? ''
  const wesperPrefillAhi = wesperAttach?.prefill.ahi
  const wesperPrefillInterpretingDate = wesperAttach?.prefill.interpretingDate
  const wesperPrefillInterpretingProviderName = wesperAttach?.prefill.interpretingProviderName
  const wesperPrefillInterpretingPhysicianId = wesperAttach?.prefill.interpretingPhysicianId ?? null

  useEffect(() => {
    if (open) {
      if (wesperSessionId) {
        const prefillDates = wesperPrefillDatesKey ? wesperPrefillDatesKey.split('|') : [undefined]
        setDatesOfService(prefillDates)
        setTestingTimes(prefillDates.map((_, i) => {
          const minutes = wesperPrefillTestingTimesKey.split('|')[i]
          return minutes ? Number(minutes) : null
        }))
        setAhi(wesperPrefillAhi || undefined)
        setInterpretingDate(wesperPrefillInterpretingDate ?? undefined)
      } else {
        setDatesOfService([undefined])
        setAhi(undefined)
        setInterpretingDate(undefined)
        setTestingTimes([null])
      }
      setRecognizedFile(null)
      setRecognition(null)
      setReusedScan(false)
      setIsRecognizing(false)
      setRecognitionNotes([])
      setInterpretingPhysician(null)
      setIncludePapScript(order?.office?.includePapScript)
      setIncludeDmeInfoInPap(order?.office?.includeDmeInfoInPap)
      setApapMin(undefined)
      setApapMax(undefined)
      setRotation(0)
      setPdfScale(1)
      setPdfLoadError(false)
      setNumPages(null)

      if (order?.followUpCarePhysicianId != null) {
        const p = order.followUpCarePhysician
        const label =
          p != null
            ? isOrganization(p?.providerType) ? p?.organizationName : [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ')
            : `Physician ${order.followUpCarePhysicianId}`
        setFollowUpCareProvider({
          value: String(order.followUpCarePhysicianId),
          label: label || `Physician ${order.followUpCarePhysicianId}`,
        })
      } else {
        setFollowUpCareProvider(null)
      }

      getInterpretingPhysicians({ physicianType: 'INTERPRETING', noOffice: 'true' })

      if (referringPhysicianNpi) {
        getFollowUpPhysicians({
          physicianNpi: referringPhysicianNpi,
          officeId: order?.officeId != null ? Number(order.officeId) : undefined,
        })
      }
    }
  }, [
    open,
    order?.office,
    order?.officeId,
    order?.followUpCarePhysicianId,
    order?.followUpCarePhysician,
    referringPhysicianNpi,
    getInterpretingPhysicians,
    getFollowUpPhysicians,
    wesperSessionId,
    wesperPrefillDatesKey,
    wesperPrefillTestingTimesKey,
    wesperPrefillAhi,
    wesperPrefillInterpretingDate,
  ])

  // Preselect the interpreting physician once the physicians directory has loaded, preferring
  // the one staff already chose on the order (matched by id, then mapped to its NPI-keyed
  // option) and falling back to a name match on whoever signed the report at Wesper. Kept
  // separate from the reset effect above (and keyed off primitives + the query result, not the
  // unstable `wesperAttach` object reference) so it can react to the physicians list arriving
  // asynchronously without re-running — and clobbering a manual selection — on every unrelated
  // parent re-render.
  useEffect(() => {
    if (!open || !wesperSessionId) {
      return
    }

    const orderPhysician = wesperPrefillInterpretingPhysicianId != null
      ? interpretingPhysiciansData?.find(
        (physician) => Number(physician.id) === Number(wesperPrefillInterpretingPhysicianId),
      )
      : undefined

    const match = orderPhysician
      ? interpretingPhysiciansOptions.find((option) => option.value === orderPhysician.npi)
      : wesperPrefillInterpretingProviderName
        ? interpretingPhysiciansOptions.find(
          (option) => option.label.toLowerCase() === wesperPrefillInterpretingProviderName.toLowerCase(),
        )
        : undefined

    if (match) {
      setInterpretingPhysician(match)
    }
  }, [
    open,
    wesperSessionId,
    wesperPrefillInterpretingPhysicianId,
    wesperPrefillInterpretingProviderName,
    interpretingPhysiciansData,
  ])

  useEffect(() => {
    if (!open || order?.followUpCarePhysicianId != null) {
      return
    }

    const onlyProvider = effectiveFollowUpPhysiciansData?.items?.length === 1
      ? effectiveFollowUpPhysiciansData.items[0]
      : null

    if (onlyProvider) {
      const p = onlyProvider.followUpPhysician
      const fullName = [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(' ')
      setFollowUpCareProvider({
        value: String(onlyProvider.followUpPhysicianId),
        label: isOrganization(p?.providerType)
          ? p?.organizationName || fullName
          : fullName || `Physician ${onlyProvider.followUpPhysicianId}`,
      })
    }
  }, [open, order?.followUpCarePhysicianId, effectiveFollowUpPhysiciansData])

  const addDateOfService = () => {
    if (datesOfService.length < MAX_DATES_OF_SERVICE) {
      setDatesOfService([...datesOfService, undefined])
      setTestingTimes([...testingTimes, null])
    }
  }

  const removeDateOfService = (index: number) => {
    if (datesOfService.length > 1) {
      setDatesOfService(datesOfService.filter((_, i) => i !== index))
      setTestingTimes(testingTimes.filter((_, i) => i !== index))
    }
  }

  const updateDateOfService = (index: number, date: string | undefined) => {
    const newDates = [...datesOfService]
    newDates[index] = date
    setDatesOfService(newDates)
  }

  const updateTestingTime = (index: number, minutes: number | null) => {
    const next = [...testingTimes]
    next[index] = minutes
    setTestingTimes(next)
  }

  // Sent alongside the dates: one entry per date, null where nothing was
  // entered, so the server keeps the two aligned.
  const testingTimesPayload = (validDates: string[]): string | null => {
    const aligned = datesOfService
      .map((date, index) => (date ? testingTimes[index] ?? null : undefined))
      .filter((value): value is number | null => value !== undefined)

    return validDates.length && aligned.some((value) => value !== null) ? JSON.stringify(aligned) : null
  }

  // Fills the form from a scan. Every recognized field stays editable; the
  // interpreting physician is matched separately, once the directory is in.
  const applyRecognition = (scan: HstReportRecognition) => {
    // Typed here: the nested back-end import behind `result` is opaque to the FE compiler.
    const result: HstReportRecognitionResult = scan.result
    const notes: string[] = []
    const nights = result.nights.slice(0, MAX_DATES_OF_SERVICE)

    if (nights.length) {
      setDatesOfService(nights.map((night) => night.dateOfService ?? undefined))
      setTestingTimes(nights.map((night) => night.totalSleepTimeMinutes))
    } else {
      setDatesOfService([undefined])
      setTestingTimes([null])
      notes.push('No dates of service were found on the report.')
    }

    if (result.nights.length > MAX_DATES_OF_SERVICE) {
      notes.push(`The report lists ${result.nights.length} nights; only the first ${MAX_DATES_OF_SERVICE} were taken.`)
    }

    setAhi(result.ahi != null ? String(result.ahi) : undefined)
    if (result.ahi == null) notes.push('No AHI was found on the report.')

    setInterpretingDate(result.interpretingDate ?? undefined)
    if (!result.interpretingDate) notes.push('No interpretation date was found on the report.')

    setInterpretingPhysician(null)
    setRecognition(scan)
    setRecognitionNotes(notes)
  }

  // The newest scan known for this order: the one on the order, or the one just
  // made here if the order has not been refetched yet (closing and reopening
  // the modal must not read the same report twice).
  const storedScan = (): HstReportRecognition | null => {
    const fromOrder = order?.hstReportRecognition ?? null
    const fromSession = latestScanRef.current
    if (!fromOrder || !fromSession) return fromOrder ?? fromSession
    return fromSession.recognizedAt >= fromOrder.recognizedAt ? fromSession : fromOrder
  }

  // Same content as the stored scan → same report, whatever it is named now.
  // Older scans without a hash fall back to name + size.
  const matchesStoredScan = (file: File, hash: string | null, scan: HstReportRecognition): boolean =>
    scan.fileHash && hash
      ? scan.fileHash === hash
      : scan.fileName === file.name && (scan.fileSize == null || scan.fileSize === file.size)

  // The NPI is the identity; the name is only a fallback for a report that
  // printed one without the other. Runs again when the directory arrives.
  useEffect(() => {
    if (!recognition || interpretingPhysician || !interpretingPhysiciansOptions.length) {
      return
    }

    const { interpretingPhysicianNpi: npi, interpretingPhysicianName: name } = recognition.result
    const byNpi = npi ? interpretingPhysiciansOptions.find((option) => option.value === npi) : undefined
    const surname = name?.split(',')[0].trim().toLowerCase()
    const byName = !byNpi && surname
      ? interpretingPhysiciansOptions.find((option) => option.label.toLowerCase().includes(surname))
      : undefined
    const match = byNpi ?? byName

    if (match) {
      setInterpretingPhysician(match)
      return
    }

    const note = npi || name
      ? `Interpreting physician ${name ?? ''}${npi ? ` (NPI ${npi})` : ''} is not in the interpreting physicians directory — select one manually.`
      : 'No interpreting physician was found on the report.'
    setRecognitionNotes((current) => (current.includes(note) ? current : [...current, note]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recognition, interpretingPhysiciansOptions.length])

  // Runs the report through AI recognition. The file is kept for the upload.
  // A report that was already scanned for this order is filled in from the
  // stored scan instead of being read again, unless a re-scan is forced.
  const handleRecognize = async (acceptedFiles: File[], force = false) => {
    const [file] = acceptedFiles
    if (!file?.name) return

    setRecognizedFile(file)
    setReusedScan(false)
    setIsRecognizing(true)
    setRecognitionNotes([])

    try {
      if (!force) {
        const previous = storedScan()
        const hash = await sha256(file)

        if (previous && matchesStoredScan(file, hash, previous)) {
          applyRecognition(previous)
          setReusedScan(true)
          return
        }
      }

      const formData = new FormData()
      formData.append('report', file)

      const scan = await recognizeReport({ orderId: Number(order.id), formData }).unwrap()
      latestScanRef.current = scan
      applyRecognition(scan)
      Toast({ type: 'success', label: 'Report recognized — please review the fields before uploading.' })
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      setRecognition(null)
      setRecognitionNotes([`Recognition failed${message ? `: ${message}` : ''}. Fill the fields in by hand; the selected file will still be uploaded.`])
      Toast({ type: 'error', label: 'Could not recognize the report.' })
    } finally {
      setIsRecognizing(false)
    }
  }

  const discardRecognition = () => {
    setRecognizedFile(null)
    setRecognition(null)
    setReusedScan(false)
    setRecognitionNotes([])
    setDatesOfService([undefined])
    setTestingTimes([null])
    setAhi(undefined)
    setInterpretingDate(undefined)
    setInterpretingPhysician(null)
  }

  const handleUpload = async (acceptedFiles: File[]) => {
    const [file] = acceptedFiles
    const storedFileKey = !file?.name && !recognizedFile ? recognition?.fileKey : undefined
    if ((!file?.name && !storedFileKey) || !reportType) return

    setIsUploading(true)

    try {
      const formData = new FormData()
      if (file?.name) {
        formData.append('report', file)
      } else if (storedFileKey) {
        // The server uploads the copy it kept from recognition.
        formData.append('recognitionFileKey', storedFileKey)
      }
      formData.append('orderId', String(order.id))
      formData.append('reportType', reportType)

      const validDates = datesOfService.filter((d): d is string => !!d)
      if (order.testType === 'PSG' && validDates[0]) {
        formData.append('dateOfService', validDates[0])
      } else if (validDates.length > 0) {
        formData.append('datesOfService', JSON.stringify(validDates))
      }

      if (ahi) {
        formData.append('ahi', ahi)
      }

      const testingTimesJson = testingTimesPayload(validDates)
      if (testingTimesJson) {
        formData.append('testingTimes', testingTimesJson)
      }

      if (interpretingPhysician) {
        formData.append('interpretingPhysicianNpi', interpretingPhysician.value)
      }
      if (interpretingDate) {
        formData.append('interpretingDate', interpretingDate)
      }
      if (reportType === 'HST') {
        formData.append('includePapScript', String(includePapScript))
        formData.append('includeDmeInfoInPap', String(includeDmeInfoInPap))
        if (apapMin) {
          formData.append('apapMin', apapMin)
        }
        if (apapMax) {
          formData.append('apapMax', apapMax)
        }
      }

      const shouldGenerateFollowUpDocuments =
        reportType === 'HST'
        && !!followUpCareProvider
        && followUpCareProviderOptions.length === 1

      if (reportType === 'HST' && followUpCareProvider) {
        formData.append('followUpCarePhysicianId', followUpCareProvider.value)
        formData.append('autoFaxFollowUpCare', String(shouldGenerateFollowUpDocuments))
      }

      await uploadReport({ orderId: Number(order.id), formData })

      if (shouldGenerateFollowUpDocuments) {
        toast.success('Document generation started. Waiting for file attaching...')
        thisStartPolling()
      }

      Toast({ type: 'success', label: 'Report uploaded!' })
      onClose()
      onUploadComplete?.()
    } catch (error) {
      Toast({ type: 'error', label: 'Error uploading report.' })
    } finally {
      setIsUploading(false)
    }
  }

  // Same field payload as handleUpload above, minus the file (the report PDF is already stored
  // from the Wesper webhook — this just attaches it via the pipeline UploadHstReportCommand runs
  // under the hood on the backend, per Task 11).
  const handleAttach = async () => {
    if (!wesperAttach) return

    setIsUploading(true)

    try {
      const validDates = datesOfService.filter((d): d is string => !!d)

      const body: {
        sessionId: string
        datesOfService: string
        ahi?: string
        testingTimes?: string
        interpretingPhysicianNpi?: string
        interpretingDate?: string
        includePapScript?: boolean
        includeDmeInfoInPap?: boolean
        apapMin?: string
        apapMax?: string
        followUpCarePhysicianId?: number
        autoFaxFollowUpCare?: boolean
      } = {
        sessionId: wesperAttach.sessionId,
        datesOfService: JSON.stringify(validDates),
      }

      if (ahi) {
        body.ahi = ahi
      }

      const testingTimesJson = testingTimesPayload(validDates)
      if (testingTimesJson) {
        body.testingTimes = testingTimesJson
      }

      if (interpretingPhysician) {
        body.interpretingPhysicianNpi = interpretingPhysician.value
      }
      if (interpretingDate) {
        body.interpretingDate = interpretingDate
      }
      if (reportType === 'HST') {
        body.includePapScript = includePapScript
        body.includeDmeInfoInPap = includeDmeInfoInPap
        if (apapMin) {
          body.apapMin = apapMin
        }
        if (apapMax) {
          body.apapMax = apapMax
        }
      }

      const shouldGenerateFollowUpDocuments =
        reportType === 'HST'
        && !!followUpCareProvider
        && followUpCareProviderOptions.length === 1

      if (reportType === 'HST' && followUpCareProvider) {
        body.followUpCarePhysicianId = Number(followUpCareProvider.value)
        body.autoFaxFollowUpCare = shouldGenerateFollowUpDocuments
      }

      await attachWesperReport({ orderId: Number(order.id), body }).unwrap()

      Toast({ type: 'success', label: 'Report uploaded!' })
      onClose()
      onUploadComplete?.()
    } catch (error) {
      Toast({ type: 'error', label: 'Error attaching report.' })
    } finally {
      setIsUploading(false)
    }
  }

  const hasGeneratedFiles = (files?: Order['files']) => {
    if (!expectedFilesRef.current || !files) {
      return false
    }

    return expectedFilesRef.current.every((expectedFile) =>
      Object.values(files).some(
        (existingFile) => existingFile.uploadType === 'OTHER' && existingFile.filename === expectedFile,
      ),
    )
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  const thisStartPolling = () => {
    stopPolling()
    expectedFilesRef.current = ['Coversheet to Order.pdf', 'Coversheet to Referring.pdf']

    let attempts = 0
    const maxAttempts = 10

    pollingRef.current = setInterval(async () => {
      attempts += 1

      try {
        const refreshedOrder = await getLabOrder(Number(order.id), false).unwrap()
        onUploadComplete?.()

        if (hasGeneratedFiles(refreshedOrder.files)) {
          stopPolling()
          expectedFilesRef.current = null
          toast.success('Document generated and attached successfully!')
          return
        }
      } catch (error) {
        console.warn('Could not refresh order while waiting for follow-up documents:', error)
      }

      if (attempts >= maxAttempts) {
        stopPolling()
        expectedFilesRef.current = null
        toast.warning('Document generation may still be in progress. Please refresh page to see the file.')
      }
    }, 3500)
  }

  useEffect(() => () => {
    stopPolling()
  }, [])

  const handleConfirmAndUpload = () => {
    // The report already picked for recognition is the report being uploaded —
    // from the browser when it was just chosen, from the stored copy otherwise.
    if (recognizedFile) {
      void handleUpload([recognizedFile])
      return
    }

    if (recognition) {
      void handleUpload([])
      return
    }

    uploadRef?.current?.click()
  }

  const requiresInterpretation = REPORT_TYPES_REQUIRING_INTERPRETATION.includes(
    reportType?.toUpperCase(),
  )

  // At least the first date of service is required. For HST, PSG and Titration
  // reports the interpreting physician and interpreting date are also required.
  const isFormValid =
    !!datesOfService[0]
    && (!requiresInterpretation || (!!interpretingPhysician && !!interpretingDate))

  // Left-column form fields — identical JSX to the pre-Wesper modal. Referenced directly for the
  // legacy (no wesperAttach) layout and inside the side-by-side flex layout when wesperAttach is
  // set, so the no-prop render path stays byte-for-byte the same (no extra wrapper elements).
  const formFieldsContent = (
    <>
      <Upload
        className="invisible"
        ref={uploadRef}
        handleUpload={handleUpload}
        invisible
        accept=".pdf"
      />

      {reportType === 'HST' && !wesperAttach && (
        <>
          <Upload
            className="invisible"
            ref={recognizeRef}
            handleUpload={(files) => handleRecognize(files)}
            invisible
            accept={{ 'application/pdf': ['.pdf'] }}
            multiple={false}
          />
          <AiPanel className={isRecognizing ? 'is-busy' : ''}>
            <div className="ai-head">
              <span className="ai-badge">
                <i className="fas fa-magic"></i>
              </span>
              <div>
                <div className="ai-title">AI Recognition</div>
                <div className="ai-subtitle">
                  {recognizedFile
                    ? isRecognizing
                      ? 'Reading the report…'
                      : reusedScan
                        ? 'This report was already scanned — the fields below are filled from that scan.'
                        : 'Review the fields below, then confirm to upload this file.'
                    : 'Pick the HST report PDF and the fields below are filled in for you to confirm.'}
                </div>
              </div>
              <div className="ai-head-actions">
                {(recognizedFile || recognition) && (
                  <Button
                    variant="link"
                    className="text-muted"
                    disabled={isRecognizing || isUploading}
                    onClick={discardRecognition}>
                    Clear
                  </Button>
                )}
                <Button
                  variant={recognizedFile || recognition ? 'outline-primary' : 'primary'}
                  disabled={isRecognizing || isUploading}
                  onClick={() => recognizeRef.current?.click()}>
                  {isRecognizing ? (
                    <>
                      <i className="fas fa-spinner fa-spin" css={{ marginRight: '0.5rem' }}></i>
                      Reading…
                    </>
                  ) : recognizedFile || recognition ? (
                    <>
                      <i className="fas fa-redo" css={{ marginRight: '0.5rem' }}></i>
                      Scan another report
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-pdf" css={{ marginRight: '0.5rem' }}></i>
                      Select report
                    </>
                  )}
                </Button>
              </div>
            </div>

            {(recognizedFile || recognition || recognitionNotes.length > 0) && (
            <div className="ai-body">
              {(recognizedFile || recognition) && (
                <div className="ai-file">
                  <span className="ai-file-icon">
                    {isRecognizing ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-file-pdf"></i>
                    )}
                  </span>
                  <div className="ai-file-text">
                    <div className="ai-file-name">{recognizedFile?.name ?? recognition?.fileName}</div>
                    <div className="ai-file-meta">
                      {isRecognizing ? (
                        'Extracting dates of service, sleep time, AHI and the interpreting physician'
                      ) : recognition ? (
                        <>
                          {reusedScan ? 'Already scanned ' : 'Scanned '}
                          {moment(recognition.recognizedAt).format('lll')}
                          {reusedScan && recognizedFile && (
                            <>
                              {' · '}
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 align-baseline"
                                disabled={isUploading}
                                onClick={() => handleRecognize([recognizedFile], true)}>
                                Scan again
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        'Could not be read — fill the fields in by hand'
                      )}
                    </div>
                  </div>
                </div>
              )}

              {recognitionNotes.length > 0 && (
                <ul className="ai-notes">
                  {recognitionNotes.map((note) => (
                    <li key={note}>
                      <i className="fas fa-exclamation-circle"></i>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}
          </AiPanel>
          <Divider />
        </>
      )}

      <SectionTitle>
        <i className="fas fa-calendar-alt" css={{ marginRight: '0.5rem' }}></i>
        Service Information
      </SectionTitle>

      <Row>
        <FormGroup>
          <Label>
            Date of Service <span className="text-danger">*</span>
            <span css={{ float: 'right', fontWeight: 400, color: '#6b7280', fontSize: 12 }}>
              Testing time (TST)
            </span>
          </Label>
          {datesOfService.map((date, index) => (
            <DateRow key={index}>
              <TimeWrapper>
                <DateTimePicker
                  value={date ? moment(date, 'YYYY-MM-DD').toDate() : null}
                  handleChange={(newDate) => {
                    updateDateOfService(
                      index,
                      newDate ? moment(newDate).format('YYYY-MM-DD') : undefined,
                    )
                  }}
                  showTimeSelect={false}
                  dateFormat="MMMM d, yyyy"
                  placeholderText={`Select date of service ${index + 1}`}
                />
              </TimeWrapper>
              {/* TST for this night: hours + minutes, stored as whole minutes. */}
              <div css={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                <Input
                  type="number"
                  name={`tstHours${index}`}
                  minValue="0"
                  maxValue="24"
                  className="form-control"
                  placeholder="h"
                  value={minutesToHours(testingTimes[index])}
                  handleChange={(e) =>
                    updateTestingTime(
                      index,
                      toTotalMinutes(e.target.value, minutesToRemainder(testingTimes[index])),
                    )
                  }
                  css={{ width: 62, height: '38px' }}
                />
                <span css={{ fontSize: 12, color: '#6b7280' }}>h</span>
                <Input
                  type="number"
                  name={`tstMinutes${index}`}
                  minValue="0"
                  maxValue="59"
                  className="form-control"
                  placeholder="m"
                  value={minutesToRemainder(testingTimes[index])}
                  handleChange={(e) =>
                    updateTestingTime(
                      index,
                      toTotalMinutes(
                        minutesToHours(testingTimes[index]),
                        String(Math.min(59, Math.max(0, Number(e.target.value) || 0))),
                      ),
                    )
                  }
                  css={{ width: 62, height: '38px' }}
                />
                <span css={{ fontSize: 12, color: '#6b7280' }}>m</span>
              </div>
              {datesOfService.length > 1 && (
                <RemoveDateButton
                  type="button"
                  onClick={() => removeDateOfService(index)}
                  title="Remove this date">
                  <i className="fas fa-times"></i>
                </RemoveDateButton>
              )}
            </DateRow>
          ))}
          {datesOfService.length < MAX_DATES_OF_SERVICE && reportType === 'HST' && (
            <AddDateButton type="button" onClick={addDateOfService}>
              <i className="fas fa-plus"></i>
              Add another date of service
            </AddDateButton>
          )}
        </FormGroup>

        <FormGroup>
          <Label>AHI</Label>
          <Input
            type="number"
            name="ahi"
            minValue="0"
            maxValue="300"
            className="form-control"
            placeholder="0-300"
            value={ahi ?? ''}
            handleChange={(e) => {
              const value = e.target.value
              setAhi(Number(value) > 300 ? '300' : value)
            }}
            css={{ height: '38px' }}
          />
        </FormGroup>
      </Row>

      <Divider />

      <SectionTitle>
        <i className="fas fa-user-md" css={{ marginRight: '0.5rem' }}></i>
        Interpretation Details
      </SectionTitle>

      <Row>
        <FormGroup>
          <Label>
            Interpreting Physician{' '}
            {requiresInterpretation && <span className="text-danger">*</span>}
          </Label>
          <Select
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Select physician..."
            name="interpretingPhysician"
            options={interpretingPhysiciansOptions}
            value={interpretingPhysician}
            onChange={(option) => setInterpretingPhysician(option)}
            isLoading={isLoadingPhysicians}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
              }),
              placeholder: (base) => ({
                ...base,
                color: '#6c757d',
              }),
            }}
          />
        </FormGroup>

        <FormGroup>
          <Label>
            Interpreting Date{' '}
            {requiresInterpretation && <span className="text-danger">*</span>}
          </Label>
          <TimeWrapper>
            <DateTimePicker
              value={
                interpretingDate ? moment(interpretingDate, 'YYYY-MM-DD').toDate() : undefined
              }
              handleChange={(date) => {
                setInterpretingDate(date ? moment(date).format('YYYY-MM-DD') : undefined)
              }}
              showTimeSelect={false}
              dateFormat="MMMM d, yyyy"
              placeholderText="Select interpretation date"
              style={{ height: '38px' }}
            />
          </TimeWrapper>
        </FormGroup>
      </Row>

      <Divider />

      <SectionTitle>
        <i className="fas fa-user-nurse" css={{ marginRight: '0.5rem' }}></i>
        Follow-Up Care (optional)
      </SectionTitle>
      <FormGroup>
        <Label>Follow-up care provider</Label>
        <Select
          className="basic-single"
          classNamePrefix="select"
          isClearable
          isSearchable
          placeholder={
            !referringPhysicianNpi
              ? 'No referring physician'
              : !hasFollowUpPhysicians
                ? 'No follow-up providers for this physician'
                : 'Select follow-up care provider...'
          }
          name="followUpCareProvider"
          options={followUpCareProviderOptions}
          value={followUpCareProvider}
          onChange={(option) => setFollowUpCareProvider(option as PhysicianOption | null)}
          isDisabled={!referringPhysicianNpi || !hasFollowUpPhysicians}
          isLoading={isLoadingFollowUpPhysicians}
          styles={{
            control: (base) => ({
              ...base,
              minHeight: '38px',
            }),
            placeholder: (base) => ({
              ...base,
              color: '#6c757d',
            }),
          }}
        />
        {referringPhysicianNpi && hasFollowUpPhysicians && (
          <small className="text-muted d-block mt-1">
            If selected, the follow-up care process will be started when you upload the report.
          </small>
        )}
      </FormGroup>

      {reportType === 'HST' && (
        <>
          <Divider />

          <SectionTitle>
            <i className="fas fa-cog" css={{ marginRight: '0.5rem' }}></i>
            Script Options
          </SectionTitle>

          <CheckboxWrapper>
            <div>
              <Form.Check
                type="checkbox"
                id="includePapScript"
                checked={includePapScript}
                onChange={(e) => setIncludePapScript(e.target.checked)}
                label="Include PAP Script in HST report"
              />
              <div className="checkbox-description">
                When enabled, the PAP script will be attached to the report
              </div>
            </div>
          </CheckboxWrapper>

          <CheckboxWrapper>
            <div>
              <Form.Check
                type="checkbox"
                id="includeDmeInfoInPap"
                checked={includeDmeInfoInPap}
                onChange={(e) => setIncludeDmeInfoInPap(e.target.checked)}
                label="Include DME Information in PAP Script"
                disabled={!includePapScript}
              />
              <div className="checkbox-description">
                When enabled, DME company contact information will be included in the attached PAP
                script
              </div>
            </div>
          </CheckboxWrapper>

          <FormGroup>
            <Label>APAP Range</Label>
            <Row style={{ alignItems: 'center', width: 'calc(50% - 1rem)' }}>
              <div>
                <Input
                  type="number"
                  name="apapMin"
                  minValue="0"
                  className="form-control"
                  placeholder="Min"
                  value={apapMin ?? ''}
                  handleChange={(e) => setApapMin(e.target.value)}
                  disabled={!includePapScript}
                />
              </div>
              <span>to</span>
              <div>
                <Input
                  type="number"
                  name="apapMax"
                  minValue="0"
                  className="form-control"
                  placeholder="Max"
                  value={apapMax ?? ''}
                  handleChange={(e) => setApapMax(e.target.value)}
                  disabled={!includePapScript}
                />
              </div>
            </Row>
          </FormGroup>
        </>
      )}
    </>
  )

  // Right-column PDF preview for the Wesper side-by-side layout. Copied from the viewer block in
  // AssignDialog.tsx (rotate controls), plus zoom in/out (AssignDialog has no zoom control; this
  // adds the same zoom pattern ReportViewer.tsx uses elsewhere for PDF review).
  const pdfViewerContent = wesperAttach && (
    <>
      <div
        css={css`
          display: flex;
          justify-content: center;
          gap: 8px;
          padding: 8px;
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          position: sticky;
          top: 0;
          z-index: 1;
        `}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setRotation((prev) => (prev - 90 + 360) % 360)}
          title="Rotate Left">
          <i className="fas fa-undo" />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setRotation((prev) => (prev + 90) % 360)}
          title="Rotate Right">
          <i className="fas fa-redo" />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setPdfScale((prev) => Math.min(prev + 0.2, 2))}
          title="Zoom In">
          <i className="fas fa-search-plus" />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setPdfScale((prev) => Math.max(prev - 0.2, 0.5))}
          title="Zoom Out">
          <i className="fas fa-search-minus" />
        </Button>
        {rotation !== 0 && (
          <span
            css={css`
              display: flex;
              align-items: center;
              font-size: 12px;
              color: #6c757d;
              margin-left: 8px;
            `}>
            Rotated {rotation}°
          </span>
        )}
      </div>
      {pdfLoadError ? (
        <div css={{ padding: '2rem', textAlign: 'center' }}>
          <p className="text-muted">
            Unable to load the report preview. The preview link may have expired.
          </p>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={async () => {
              try {
                await getLabOrder(Number(order.id), false).unwrap()
                setPdfLoadError(false)
              } catch (error) {
                console.warn('Could not refresh order to renew the Wesper preview URL:', error)
              }
            }}>
            Refresh preview
          </Button>
        </div>
      ) : (
        <Document
          loading={'Loading PDF ...'}
          file={wesperAttach.fileUrl}
          onLoadSuccess={({ numPages: loadedNumPages }) => setNumPages(loadedNumPages)}
          onLoadError={() => setPdfLoadError(true)}
          externalLinkTarget="_blank">
          {Array.from({ length: numPages ?? 0 }, (_, i) => i + 1).map((pageNumber) => (
            <Page loading="" pageNumber={pageNumber} scale={pdfScale} rotate={rotation} key={pageNumber} />
          ))}
        </Document>
      )}
    </>
  )

  return (
    <Modal show={open} onHide={onClose} size={wesperAttach ? 'xl' : 'lg'} centered>
      <Modal.Header>
        <Modal.Title>
          <i
            className="fas fa-file-medical"
            css={{ fontSize: '1.5rem', marginRight: '0.5rem', color: '#2c83bd' }}></i>
          {wesperAttach
            ? 'Review & Attach Wesper Report'
            : reportType === 'HST'
              ? 'Upload HST Report'
              : reportType === 'FAILED_HST'
                ? 'Upload Failed HST Report'
                : `Upload ${reportType} Report`}
        </Modal.Title>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>
          <span css={{ fontSize: '1.5rem' }} aria-hidden="true">
            &times;
          </span>
        </button>
      </Modal.Header>

      <Modal.Body css={wesperAttach ? css`display: flex; padding: 0;` : { padding: '1.5rem' }}>
        {isRecognizing && (
          <div
            css={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.6)',
              zIndex: 2,
              cursor: 'progress',
            }}
          />
        )}
        {wesperAttach ? (
          <>
            <div
              css={css`
                flex: 1;
                min-width: 0;
                padding: 1.5rem;
                max-height: 80vh;
                overflow-y: auto;
              `}>
              {formFieldsContent}
            </div>
            <div
              css={css`
                flex: 1;
                min-width: 0;
                border-left: 1px solid #dee2e6;
                max-height: 80vh;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
              `}>
              {pdfViewerContent}
            </div>
          </>
        ) : (
          formFieldsContent
        )}
      </Modal.Body>

      <Modal.Footer>
        <div
          css={{
            flex: 1,
            fontSize: '14px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
          }}>
          {!isFormValid && (
            <>
              <i className="fas fa-info-circle" css={{ marginRight: '0.5rem' }}></i>
              Please fill in the required fields to proceed
            </>
          )}
        </div>
        <Button variant="outline-secondary" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={wesperAttach ? handleAttach : handleConfirmAndUpload}
          disabled={!isFormValid || isUploading || isRecognizing}>
          {isUploading ? (
            <>
              <i className="fas fa-spinner fa-spin" css={{ marginRight: '0.5rem' }}></i>
              {wesperAttach ? 'Attaching...' : 'Uploading...'}
            </>
          ) : wesperAttach ? (
            <>
              <i className="fas fa-paperclip" css={{ marginRight: '0.5rem' }}></i>
              Attach Report
            </>
          ) : recognizedFile || recognition ? (
            <>
              <i className="fas fa-upload" css={{ marginRight: '0.5rem' }}></i>
              Confirm & Upload
            </>
          ) : (
            <>
              <i className="fas fa-upload" css={{ marginRight: '0.5rem' }}></i>
              Select File & Upload
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
